import { OverlayPortal as FloatingPortal } from "@/components/modal/OverlayPortal";
import { Icon, IconName } from "@/components/icons/Icon";
import { Button } from "@/components/interactive/Button";
import {
  ButtonV2,
  type ButtonV2IconPlacement,
  type ButtonV2Size,
  type ButtonV2Variant,
  IconButtonV2,
  type IconButtonV2Size,
  type IconButtonV2Variant,
} from "@/components/interactive/ButtonV2";
import { MenuContainerV2, MenuSectionV2 } from "@/components/interactive/MenuContainerV2";
import {
  MENU_SUBMENU_LAYER_SELECTOR,
  MenuItemV2,
  MenuItemV2Props,
} from "@/components/interactive/MenuItemV2";
import { TooltipV2 } from "@/components/TooltipV2";
import { ToolbarIconButtonV2 } from "@/components/interactive/ToolbarIconButtonV2";
import {
  announceDropdownMenuOpen,
  subscribeToOtherDropdownMenuOpen,
} from "@/components/interactive/dropdownMenuCoordination";
import {
  type UseDropdownFloatingV2Options,
  useDropdownFloatingV2,
} from "@/components/interactive/use-dropdown-floating-v2";
import { EMPTY_OBJECT } from "@/constants/stable-empty";
import { ZIndex, ZIndexClass } from "@/constants/z-index";
import { classNames } from "@/utils/classnames";

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import React, {
  Fragment,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";

interface IconButtonDropdownMenuV2Props {
  children: ReactNode;
  /** Fully custom trigger element (must forward refs, e.g. a plain <button>). Sized by its
   * container instead of inline-flex, and overrides all button* chrome props. */
  trigger?: ReactNode;
  buttonIcon?: IconName;
  buttonIconPlacement?: ButtonV2IconPlacement;
  buttonClassName?: string;
  buttonChrome?: "icon" | "button";
  buttonButtonVariant?: ButtonV2Variant;
  buttonDisabled?: boolean;
  /** Override the accessible name of a labeled trigger without changing its visible text. */
  buttonAriaLabel?: string;
  buttonLabel?: string;
  buttonTestId?: string;
  buttonSize?: IconButtonV2Size;
  buttonText?: string;
  buttonTooltip?: ReactNode;
  buttonVariant?: IconButtonV2Variant;
  className?: string;
  closeOnMouseLeave?: boolean;
  menuClassName?: string;
  width?: number | string;
  /**
   * Placement, strategy and trigger gap for the popup. The height cap is
   * deliberately not exposed: a menu that outgrows the room its trigger has
   * should scroll, never overflow the viewport.
   */
  floatOptions?: Pick<UseDropdownFloatingV2Options, "placement" | "strategy" | "offset">;
  /** Open the menu from where the trigger was clicked instead of from its edge, for a trigger too
   * large for an edge to be near the pointer. A keyboard open still uses the trigger's edge. */
  anchorToPointer?: boolean;
}

/** Pointer slack around the trigger/menu before an open menu closes; covers the float offset gap. */
const CLOSE_ON_LEAVE_GRACE_PX = 24;

/**
 * The height cap travels as a custom property rather than an inline
 * `max-height`, because the submenu carve-out below has to be able to drop it
 * and a class can never beat an inline style.
 */
type MenuSurfaceStyle = React.CSSProperties & {
  "--dropdown-menu-max-height"?: string;
};

/**
 * A menu taller than the room its trigger has scrolls inside that room instead
 * of running off the viewport.
 *
 * Rows that open a flyout submenu are the exception. The submenu renders as an
 * `absolute left-full` DOM child of the row, so a scroll container clips it out
 * of reach entirely — worse than the overflow the cap prevents. `aria-haspopup`
 * marks exactly those rows, so a menu that can fan out keeps its overflow
 * visible and grows past the cap instead.
 */
const MENU_SURFACE_CLAMP_CLASS =
  "max-h-[var(--dropdown-menu-max-height,none)] overflow-y-auto " +
  "has-[[aria-haspopup]]:max-h-none has-[[aria-haspopup]]:overflow-y-visible";

const pointerWithin = (el: Element | null, e: PointerEvent, margin: number): boolean => {
  if (el == null) return false;
  // Submenus are positioned outside the parent menu's bounding box but remain DOM descendants.
  // Treat them as inside so moving into a submenu does not dismiss the whole menu.
  if (e.target instanceof Node && el.contains(e.target)) return true;
  const rect = el.getBoundingClientRect();
  return (
    e.clientX >= rect.left - margin &&
    e.clientX <= rect.right + margin &&
    e.clientY >= rect.top - margin &&
    e.clientY <= rect.bottom + margin
  );
};

interface HoverRevealToolbarContextValue {
  setMenuOpen: (isOpen: boolean) => void;
}

const HoverRevealToolbarContext = React.createContext<HoverRevealToolbarContextValue | null>(null);

export const HoverRevealToolbarV2: React.FC<{
  children: ReactNode;
  className?: string;
  revealClassName?: string;
}> = ({
  children,
  className,
  revealClassName = "group-hover:w-auto group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:w-auto group-focus-within:pointer-events-auto group-focus-within:opacity-100",
}) => {
  const [openMenuCount, setOpenMenuCount] = useState(0);
  const setMenuOpen = useCallback((isOpen: boolean) => {
    setOpenMenuCount((count) => Math.max(0, count + (isOpen ? 1 : -1)));
  }, []);
  const visibilityContext = useMemo(() => ({ setMenuOpen }), [setMenuOpen]);
  const isPinnedOpen = openMenuCount > 0;

  return (
    <HoverRevealToolbarContext.Provider value={visibilityContext}>
      <div
        className={classNames(
          "flex w-0 items-center gap-0 overflow-hidden opacity-0 transition-opacity duration-75",
          "pointer-events-none",
          revealClassName,
          isPinnedOpen && "w-auto pointer-events-auto opacity-100",
          className,
        )}
      >
        {children}
      </div>
    </HoverRevealToolbarContext.Provider>
  );
};

export const IconButtonDropdownMenuV2: React.FC<IconButtonDropdownMenuV2Props> = ({
  children,
  trigger,
  buttonIcon = "more",
  buttonIconPlacement,
  buttonClassName,
  buttonChrome,
  buttonButtonVariant,
  buttonDisabled,
  buttonAriaLabel,
  buttonLabel = "Tools",
  buttonTestId,
  buttonSize,
  buttonText,
  buttonTooltip = buttonLabel,
  buttonVariant,
  className,
  closeOnMouseLeave = true,
  menuClassName,
  width = 196,
  floatOptions = EMPTY_OBJECT,
  anchorToPointer = false,
}) => {
  return (
    <Menu>
      {({ open, close }) => (
        <IconButtonDropdownMenuV2Inside
          open={open}
          close={close}
          trigger={trigger}
          buttonIcon={buttonIcon}
          buttonIconPlacement={buttonIconPlacement}
          buttonClassName={buttonClassName}
          buttonChrome={buttonChrome}
          buttonButtonVariant={buttonButtonVariant}
          buttonDisabled={buttonDisabled}
          buttonAriaLabel={buttonAriaLabel}
          buttonLabel={buttonLabel}
          buttonTestId={buttonTestId}
          buttonSize={buttonSize}
          buttonText={buttonText}
          buttonTooltip={buttonTooltip}
          buttonVariant={buttonVariant}
          className={className}
          closeOnMouseLeave={closeOnMouseLeave}
          menuClassName={menuClassName}
          width={width}
          floatOptions={floatOptions}
          anchorToPointer={anchorToPointer}
        >
          {children}
        </IconButtonDropdownMenuV2Inside>
      )}
    </Menu>
  );
};

const IconButtonDropdownMenuV2Inside: React.FC<
  IconButtonDropdownMenuV2Props & {
    open: boolean;
    close: () => void;
  }
> = ({
  children,
  open,
  close,
  trigger,
  buttonIcon = "more",
  buttonIconPlacement,
  buttonClassName,
  buttonChrome = "icon",
  buttonButtonVariant = "secondary",
  buttonDisabled,
  buttonAriaLabel,
  buttonLabel = "Tools",
  buttonTestId,
  buttonSize,
  buttonText,
  buttonTooltip = buttonLabel,
  buttonVariant,
  className,
  closeOnMouseLeave = true,
  menuClassName,
  width = 196,
  floatOptions = EMPTY_OBJECT,
  anchorToPointer = false,
}) => {
  const dropdownId = useId();
  const menuChildrenContainSection = useMemo(() => containsMenuSection(children), [children]);
  const toolbarVisibility = useContext(HoverRevealToolbarContext);
  const textButtonSize: ButtonV2Size =
    buttonSize === "3xs" || buttonSize === "2xs" ? "xs" : buttonSize ?? "sm";
  // `strategy` sits before the spread so `floatOptions` can override it. Absolute
  // is the default because these triggers are laid out in normal flow, so the
  // menu tracks the page rather than the viewport as it scrolls.
  const { refs, floatingStyles, menuMaxHeight } = useDropdownFloatingV2({
    open,
    strategy: "absolute",
    ...floatOptions,
  });

  useEffect(() => {
    if (!open) return;

    toolbarVisibility?.setMenuOpen(true);
    return () => toolbarVisibility?.setMenuOpen(false);
  }, [open, toolbarVisibility]);

  useEffect(() => subscribeToOtherDropdownMenuOpen(dropdownId, close), [close, dropdownId]);

  useEffect(() => {
    if (open) {
      announceDropdownMenuOpen(dropdownId);
    }
  }, [dropdownId, open]);

  // The MenuItems' onMouseLeave can't close a menu the pointer never entered (opened, then moused
  // straight away past the popup). Track the pointer while open; close once it leaves trigger + menu.
  useEffect(() => {
    if (!open || !closeOnMouseLeave) return;
    // Armed only after the pointer is first seen inside, so a keyboard-opened menu keeps its
    // click-outside/Escape semantics while the pointer happens to be parked elsewhere.
    let pointerWasInside = false;
    const handlePointerMove = (e: PointerEvent) => {
      // The DOM trigger, not the position reference, which is a point when anchored to the pointer.
      const reference = refs.domReference.current;
      const floating = refs.floating.current;
      // Flyout submenus overhang the menu's box, so count their layers as inside —
      // otherwise navigating deep into a submenu reads as leaving the dropdown.
      const submenuLayers = floating?.querySelectorAll(MENU_SUBMENU_LAYER_SELECTOR);
      const inside =
        (reference instanceof Element && pointerWithin(reference, e, CLOSE_ON_LEAVE_GRACE_PX)) ||
        pointerWithin(floating, e, CLOSE_ON_LEAVE_GRACE_PX) ||
        [...(submenuLayers ?? [])].some((el) => pointerWithin(el, e, CLOSE_ON_LEAVE_GRACE_PX));
      if (inside) {
        pointerWasInside = true;
      } else if (pointerWasInside) {
        close();
      }
    };
    document.addEventListener("pointermove", handlePointerMove);
    return () => document.removeEventListener("pointermove", handlePointerMove);
  }, [open, closeOnMouseLeave, close, refs.domReference, refs.floating]);

  // The anchor is kept as an offset into the trigger and re-read from its live rect, so the menu
  // stays on the clicked spot as the page scrolls; `contextElement` lets autoUpdate track that.
  const anchorAtPointer = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const trigger = event.currentTarget;
      const start = trigger.getBoundingClientRect();
      const dx = event.clientX - start.left;
      const dy = event.clientY - start.top;
      refs.setPositionReference({
        contextElement: trigger,
        getBoundingClientRect: () => {
          const rect = trigger.getBoundingClientRect();
          return new DOMRect(rect.left + dx, rect.top + dy, 0, 0);
        },
      });
    },
    [refs],
  );

  // Crossing the small gutter into a flyout submenu fires mouseleave on the menu container;
  // while a submenu is open, defer to the pointer tracker above, which counts submenu layers.
  const handleMenuMouseLeave = useCallback(() => {
    if (refs.floating.current?.querySelector(MENU_SUBMENU_LAYER_SELECTOR) != null) return;
    close();
  }, [close, refs.floating]);

  const menuSurfaceStyle: MenuSurfaceStyle = {
    "--dropdown-menu-max-height": menuMaxHeight == null ? undefined : `${menuMaxHeight}px`,
  };

  const button =
    trigger != null ? (
      // Headless UI clones the child with the trigger props (onClick, ref, aria-*).
      <MenuButton as={Fragment} disabled={buttonDisabled}>
        {trigger}
      </MenuButton>
    ) : buttonText != null && buttonChrome === "button" ? (
      // A labeled ButtonV2 trigger (variant honored); the legacy labeled branch below keeps the old
      // white Button with a trailing icon for existing callers.
      <MenuButton
        as={ButtonV2}
        type={"button"}
        icon={buttonIcon}
        iconPlacement={buttonIconPlacement}
        size={textButtonSize}
        variant={buttonButtonVariant}
        disabled={buttonDisabled}
        aria-label={buttonAriaLabel}
        aria-haspopup={"menu"}
        aria-expanded={open}
        data-testid={buttonTestId}
        className={buttonClassName}
      >
        {buttonText}
      </MenuButton>
    ) : buttonText == null && buttonChrome === "button" ? (
      <TooltipV2 content={open ? null : buttonTooltip} placement={"bottom"}>
        <MenuButton
          as={ButtonV2}
          type={"button"}
          icon={buttonIcon}
          size={textButtonSize}
          variant={buttonButtonVariant}
          disabled={buttonDisabled}
          aria-label={buttonAriaLabel ?? buttonLabel}
          aria-haspopup={"menu"}
          aria-expanded={open}
          data-testid={buttonTestId}
          className={buttonClassName}
        />
      </TooltipV2>
    ) : buttonText == null && buttonVariant == null ? (
      <MenuButton
        as={ToolbarIconButtonV2}
        icon={buttonIcon}
        disabled={buttonDisabled}
        aria-label={buttonLabel}
        aria-haspopup={"menu"}
        aria-expanded={open}
        data-testid={buttonTestId}
        tooltip={open ? null : buttonTooltip}
      />
    ) : buttonText == null ? (
      <TooltipV2 content={open ? null : buttonTooltip} placement={"bottom"}>
        <MenuButton
          as={IconButtonV2}
          type={"button"}
          icon={buttonIcon}
          size={buttonSize ?? "sm"}
          variant={buttonVariant}
          disabled={buttonDisabled}
          aria-label={buttonLabel}
          aria-haspopup={"menu"}
          aria-expanded={open}
          data-testid={buttonTestId}
          className={buttonClassName}
        />
      </TooltipV2>
    ) : (
      <MenuButton
        as={Button}
        type={"button"}
        color={"white"}
        disabled={buttonDisabled}
        className={"flex items-center gap-2"}
        aria-label={buttonLabel}
        aria-haspopup={"menu"}
        aria-expanded={open}
      >
        <div>{buttonText}</div>
        <Icon name={buttonIcon} />
      </MenuButton>
    );

  return (
    <div className={className}>
      <div
        ref={refs.setReference}
        // A custom trigger fills its container (e.g. a grid cell); the chrome buttons stay inline.
        className={trigger != null ? "h-full w-full" : "inline-flex"}
        onPointerDownCapture={(event) => {
          if (!open) {
            announceDropdownMenuOpen(dropdownId);
            if (anchorToPointer) anchorAtPointer(event);
          }
        }}
        // A keyboard open has no pointer, so it drops any earlier click point for the trigger itself.
        onKeyDownCapture={anchorToPointer ? () => refs.setPositionReference(null) : undefined}
      >
        {button}
      </div>
      <FloatingPortal>
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            zIndex: ZIndex.POPOVER,
            width,
          }}
          className={classNames(ZIndexClass.POPOVER, "design-system-typography")}
          // The menu portals out of the DOM tree but not the React tree, so item
          // clicks would otherwise bubble to whatever hosts the trigger — e.g. a
          // table row wrapped in a legacy-behavior Link, which treats any
          // unhandled click as row navigation. A click inside the menu is a menu
          // selection, never a click on the surface beneath the trigger.
          role="presentation"
          onClick={(event) => event.stopPropagation()}
        >
          <MenuItems
            as={MenuContainerV2}
            onKeyDownCapture={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              const activeId = event.currentTarget.getAttribute("aria-activedescendant");
              const item =
                event.target === event.currentTarget
                  ? activeId == null
                    ? null
                    : document.getElementById(activeId)
                  : event.target;
              // Headless UI closes before dispatching a keyboard click; stateful menu rows
              // need the same keep-open behavior for keyboard and pointer activation.
              if (
                item instanceof HTMLButtonElement &&
                item.dataset.menuKeepOpen === "true" &&
                !item.disabled
              ) {
                event.preventDefault();
                event.stopPropagation();
                item.click();
              }
            }}
            // A dropdown is not a dialog: it already closes on outside click and Escape, so
            // it does not need Headless UI's document scroll lock. The lock is actively
            // wrong here, because it pairs `overflow: hidden` on <html> with a
            // padding-right equal to `innerWidth - clientWidth`. With the app's
            // `scrollbar-gutter: stable` (styles/overflow.css) that difference is the
            // reserved gutter rather than a real scrollbar, so opening any menu swapped the
            // gutter for real padding and shifted the page in by ~15px.
            modal={false}
            onMouseLeave={closeOnMouseLeave ? handleMenuMouseLeave : undefined}
            style={menuSurfaceStyle}
            // Headless UI focuses the panel on open; suppress the browser's default focus ring so
            // the menu doesn't render with a blue outline (items own their own focus treatment).
            // The clamp sits before `menuClassName` so a caller's own `max-h-*` replaces it.
            className={classNames(
              "w-full focus:outline-none",
              MENU_SURFACE_CLAMP_CLASS,
              menuClassName,
            )}
          >
            {menuChildrenContainSection ? children : <MenuSectionV2>{children}</MenuSectionV2>}
          </MenuItems>
        </div>
      </FloatingPortal>
    </div>
  );
};

const containsMenuSection = (children: ReactNode): boolean =>
  React.Children.toArray(children).some((child) => {
    if (!React.isValidElement(child)) return false;
    if (child.type === MenuSectionV2) return true;
    if (child.type !== Fragment) return false;
    return containsMenuSection((child.props as { children?: ReactNode }).children);
  });

export interface IconButtonDropdownMenuItemV2Props extends MenuItemV2Props {
  /** Keep the menu open after selecting stateful controls like checkbox rows. */
  closeOnSelect?: boolean;
  /** Nested menu item props rendered in a flyout submenu. */
  submenuItems?: readonly IconButtonDropdownMenuItemV2Props[];
  /** Style this item as a destructive action. */
  destructive?: boolean;
}

const DESTRUCTIVE_MENU_ITEM_CLASSES = "!text-red-500 [&_.menu-item-v2-left-icon]:!text-red-500";

export const IconButtonDropdownMenuItemV2: React.FC<IconButtonDropdownMenuItemV2Props> = ({
  active: activeFromProps,
  className,
  highlighted: highlightedFromProps,
  closeOnSelect = true,
  destructive = false,
  href,
  intent,
  onClick,
  submenu,
  submenuItems,
  ...props
}) => {
  const handleClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      if (!closeOnSelect) {
        event.preventDefault();
      }

      onClick?.(event);
    },
    [closeOnSelect, onClick],
  );

  const renderedSubmenu =
    submenu ??
    (submenuItems != null && submenuItems.length > 0 ? (
      <MenuContainerV2 className={"min-w-48"}>
        <MenuSectionV2>
          {submenuItems.map((item, index) => (
            <IconButtonDropdownMenuItemV2
              {...item}
              key={typeof item.label === "string" ? item.label : index}
            />
          ))}
        </MenuSectionV2>
      </MenuContainerV2>
    ) : undefined);

  return (
    <MenuItem disabled={props.disabled} as={Fragment}>
      {({ active }) => (
        <MenuItemV2
          size={"sm"}
          active={activeFromProps}
          highlighted={highlightedFromProps || active}
          href={renderedSubmenu == null ? href : undefined}
          onClick={renderedSubmenu == null ? handleClick : undefined}
          submenu={renderedSubmenu}
          className={classNames(className, destructive && DESTRUCTIVE_MENU_ITEM_CLASSES)}
          intent={intent}
          data-menu-keep-open={!closeOnSelect || undefined}
          {...props}
        />
      )}
    </MenuItem>
  );
};
